import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Account } from '@/domain/accounts/account'
import { Agreement, UpdateAgreementData } from '@/domain/agreements/agreement'
import { Offering } from '@/domain/offerings/offering'
import { useDeathDataByContract } from '@/hooks/api/useDeathData'
import { useParcelByContract, useParcelContractDetail } from '@/hooks/api/useParcels'
import { ChevronDown, ChevronUp, User } from 'lucide-react'
import { useState } from 'react'
import { AgreementPaymentsInfo } from './AgreementPaymentsInfo'
import { DocumentsCard } from './documents/DocumentsCard'
import { AccountStatusCard } from './main-components/AccountStatusCard'
import { AgreementDetailsHeader } from './main-components/AgreementDetailsHeader'
import { DeathDataList } from './main-components/DeathDataList'
import { ParcelInfoSection } from './main-components/ParcelInfoSection'

export interface ParcelsCmpProps {
  agreement: Agreement
  offering?: Offering
  contractAccount?: Account
  userAccounts?: Account[]
  totalBalance: number
  isLoadingAccounts: boolean
  onViewAccountMovements: () => void
  isEditing?: boolean
  isSaving?: boolean
  onSave?: (changes: UpdateAgreementData) => void
  onCancel?: () => void
  onEdit?: () => void
}

export const ParcelsCmp = (props: ParcelsCmpProps) => {
  const {
    agreement, offering, contractAccount, userAccounts, totalBalance, isLoadingAccounts,
    onViewAccountMovements, isEditing, isSaving, onSave, onCancel, onEdit
  } = props

  const { data: parcel } = useParcelByContract({ contractId: agreement.id })
  const { data: detail, isLoading, error } = useParcelContractDetail({ contractId: agreement.id, parcelId: parcel?.id || '' })
  const { data: deathData } = useDeathDataByContract(agreement.id)
  const [showDeathData, setShowDeathData] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <AgreementDetailsHeader
            agreement={agreement}
            offering={offering}
            totalBalance={totalBalance}
            expensesCode={detail?.expensesCode}
            actions={{
              isEditing,
              isSaving,
              onSave,
              onCancel,
              onEdit
            }}
            data={{
              userAccounts,
              contractAccount,
              isLoadingAccounts,
            }}
          />
          <CardContent className="space-y-6">
            {parcel && <ParcelInfoSection parcel={parcel} agreement={agreement} />}

            {deathData && deathData.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <Button
                  variant="ghost"
                  className="w-full flex items-center justify-between p-4 h-auto hover:bg-muted/50 rounded-xl border border-dashed border-border/60 transition-all group"
                  onClick={() => setShowDeathData(!showDeathData)}
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-full group-hover:bg-primary/20 transition-colors">
                      <User className="h-4 w-4 text-primary flex-shrink-0" />
                    </div>
                    <div className="flex flex-col items-start text-left">
                      <span className="font-bold text-sm">Información de Fallecidos</span>
                      <span className="text-xs text-muted-foreground">{deathData.length} persona{deathData.length > 1 ? 's' : ''} registrada{deathData.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  {showDeathData ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </Button>

                {showDeathData && (
                  <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                    <DeathDataList deathData={deathData} serviceType={offering?.serviceType} />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {contractAccount && (
            <AccountStatusCard
              contractAccount={contractAccount}
              onViewMovements={onViewAccountMovements}
            />
          )}
          <DocumentsCard agreementId={agreement.id} />
        </div>
      </div>
      <AgreementPaymentsInfo
        agreement={agreement}
        serviceType={offering?.serviceType}
      />
    </div>
  )
}